import React, { useState, useContext } from 'react';
import { CircularProgress } from '@material-ui/core';
import { Context as AuthContext } from '../../../contexts/authContext';
import { CheckBoxTermOfUse } from '../../Controllers/CheckBoxTermOfUse/CheckBoxTermOfUse';
import { Register2FAModal } from './Register2FAModal';
import { UserRole } from '../../../types';
import { MDInput } from 'components/MDInput';
import { MDBox } from 'components/MDBox';
import { Switch } from '@mui/material';
import { MDTypography } from 'components/MDTypography';
import { MDButton } from 'components/MDButton';

export default function RegisterFormBusiness() {
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [repassword, setRePassword] = useState('');
  const [isPasswordMatch, setIsPasswordMatch] = useState(true);
  const { register, errors, isLoading, clearState } = useContext(AuthContext);
  const [disable, setDisable] = useState(true);
  const [open2FA, setOpen2FA] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const shouldDisableRegister =
    !email ||
    !password ||
    !companyName ||
    !fullName ||
    !phone ||
    !isPasswordMatch ||
    disable;

  const onRegister = async () => {
    if (password !== repassword) {
      setIsPasswordMatch(false);
      return;
    }
    if (register) {
      const response = await register(
        email,
        password,
        UserRole.admin,
        companyName,
        fullName,
        phone
        // twoFactorMethod
      );
      if (response && !response.token) {
        setSuccessMessage(
          response.message || 'Please check your email to verify your account.'
        );
      }
    }
  };

  const onFocus = () => {
    if (clearState) {
      clearState();
    }
  };

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const on2FAOptionChooseHandler = async (twoFactorMethod: string) => {
    if (password !== repassword) {
      setIsPasswordMatch(false);
      return;
    }
    if (!email || !password || !companyName || !fullName || !phone) {
      return;
    }
    if (register) {
      await register(
        email,
        password,
        UserRole.admin,
        companyName,
        fullName,
        phone,
        twoFactorMethod
      );
      setOpen2FA(false);
    }
  };

  const err = errors?.errors as any; // TODO: refactor auth context - not fit for new error responses
  // console.log('err', err);

  if (successMessage) {
    return (
      <MDBox textAlign="center" py={4}>
        <MDTypography variant="h5" fontWeight="medium" gutterBottom>
          Registration Successful
        </MDTypography>
        <MDTypography variant="body2" color="text">
          {successMessage}
        </MDTypography>
        <MDBox mt={3}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => window.location.href = '/login'}
          >
            Back to Login
          </MDButton>
        </MDBox>
      </MDBox>
    );
  }

  return (
    <div>
      <MDBox mb={1}>
        <MDInput
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="email"
          label="Admin Email"
          name="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={Boolean(err && err.find((x: any) => x.field === 'email'))}
          helperText={
            (err && err.find((x: any) => x.field === 'email'))?.message
          }
          onFocus={onFocus}
        />
      </MDBox>

      <MDBox mb={1}>
        <MDInput
          variant="outlined"
          margin="normal"
          fullWidth
          required
          id="companyName"
          label="Company Name"
          name="companyName"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
        />
      </MDBox>

      <MDBox mb={1}>
        <MDInput
          variant="outlined"
          margin="normal"
          fullWidth
          required
          id="fullName"
          label="Full Name"
          name="fullName"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
      </MDBox>

      <MDBox mb={1}>
        <MDInput
          variant="outlined"
          margin="normal"
          fullWidth
          required
          id="phone"
          label="Phone Number"
          name="phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={Boolean(err && err.find((x: any) => x.field === 'phone'))}
          helperText={
            (err && err.find((x: any) => x.field === 'phone'))?.message
          }
        />
      </MDBox>

      <MDBox mb={1}>
        <MDInput
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type="password"
          id="password"
          autoComplete="current-password"
          value={password}
          error={Boolean(err && err.find((x: any) => x.field === 'password'))}
          helperText={
            (err && err.find((x: any) => x.field === 'password'))?.message
          }
          onFocus={onFocus}
          onChange={(e) => setPassword(e.target.value)}
        />
      </MDBox>

      <MDBox mb={1}>
        <MDInput
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="repassword"
          label="Renter Password"
          type="password"
          id="repassword"
          autoComplete="current-password"
          value={repassword}
          onChange={(e) => setRePassword(e.target.value)}
          error={!isPasswordMatch}
          helperText={!isPasswordMatch ? 'Passwords do not match' : undefined}
          onFocus={() => setIsPasswordMatch(true)}
        />
      </MDBox>

      {/* <FormControlLabel control={<Checkbox value="remember" color="primary" />} label="Remember me" /> */}
      <MDBox display="flex" alignItems="center" ml={-1}>
        <Switch checked={rememberMe} onChange={handleSetRememberMe} />
        <MDTypography
          variant="button"
          fontWeight="regular"
          color="text"
          onClick={handleSetRememberMe}
          sx={{ cursor: 'pointer', userSelect: 'none', ml: -1 }}
        >
          &nbsp;&nbsp;Remember me
        </MDTypography>
      </MDBox>
      <MDButton
        variant="gradient"
        color="info"
        size="large"
        fullWidth
        onClick={onRegister}
        disabled={isLoading || shouldDisableRegister}
      >
        {!isLoading ? 'Register' : <CircularProgress color="inherit" />}
      </MDButton>
      <CheckBoxTermOfUse onAgreeChange={(isAgree) => setDisable(isAgree)} />
      {/* <Register2FAModal
        open={open2FA}
        onClose={() => setOpen2FA(false)}
        onSubmit={on2FAOptionChooseHandler}
        email={email}
        phone={phone}
      /> */}
    </div>
  );
}
